import { useEffect, useId, useState } from 'react';
import type { PersonDTO } from '@almanac/shared';
import { squareAvatarBlob } from '../lib/avatarImage.js';
import { useObligations, useDeleteObligation } from '../lib/obligations.js';
import { useDocuments } from '../lib/documents.js';
import {
  describeOccasion,
  personDocuments,
  personOccasions,
  useCreatePerson,
  useDeletePerson,
  useRemovePersonAvatar,
  useSetPersonAvatar,
  useUpdatePerson,
} from '../lib/people.js';
import { PersonAvatar } from './PersonAvatar.js';
import { PersonPicker } from './PersonPicker.js';

interface Props {
  /** An existing person to edit, `'new'` to create, or `null` when closed. */
  target: PersonDTO | 'new' | null;
  onClose: () => void;
  /** Called after a delete completes, so hosts can also close the card. */
  onDeleted?: () => void;
}

interface Draft {
  displayName: string;
  relationship: string;
  relatedPersonIds: string[];
  notes: string;
  household: boolean;
}

/**
 * The one person editor in the app — a bottom sheet matching the other
 * edit drawers. The People page, the person card, and the obligation
 * drawer all open this same sheet, replacing the old form-inside-a-form
 * inline editor.
 *
 * Occasions are shown read-only: their dates belong to the occasion
 * entries themselves (Obligations). Deleting a person also removes the
 * occasions that are only about them (a birthday without its person is
 * meaningless); documents keep their records and simply file under
 * "Unassigned" in the by-person view.
 */
export function PersonEditSheet({ target, onClose, onDeleted }: Props) {
  const obligations = useObligations();
  const documents = useDocuments();
  const create = useCreatePerson();
  const update = useUpdatePerson();
  const delPerson = useDeletePerson();
  const delObligation = useDeleteObligation();
  const setAvatar = useSetPersonAvatar();
  const removeAvatar = useRemovePersonAvatar();
  // Real <label htmlFor> so the OS photo picker opens natively on tap
  // (a JS .click() on a hidden input is ignored in the iOS PWA).
  const photoInputId = useId();

  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;

  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  async function pickPhoto(file: File | undefined) {
    if (!file || !existing) return;
    setPhotoError(null);
    try {
      const blob = await squareAvatarBlob(file);
      setAvatar.mutate({ id: existing.id, blob });
    } catch {
      setPhotoError('Could not read that image — try a different photo.');
    }
  }

  useEffect(() => {
    if (target === null) {
      setDraft(null);
    } else if (target === 'new') {
      setDraft({
        displayName: '',
        relationship: '',
        relatedPersonIds: [],
        notes: '',
        household: false,
      });
    } else {
      setDraft({
        displayName: target.displayName,
        relationship: target.relationship ?? '',
        relatedPersonIds: target.relatedPersonIds,
        notes: target.notes ?? '',
        household: target.household,
      });
    }
    setConfirmingDelete(false);
    // Re-seed only when the subject changes, not on every cache refresh
    // of the same person mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target === 'new' ? 'new' : target?.id]);

  useEffect(() => {
    if (!target) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (confirmingDelete) setConfirmingDelete(false);
      else onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, confirmingDelete, onClose]);

  if (!target || !draft) return null;

  const linkedOccasions = existing
    ? personOccasions(obligations.data ?? [], existing.id)
    : [];
  const linkedDocsCount = existing
    ? personDocuments(documents.data ?? [], existing.id).length
    : 0;

  const trimmedName = draft.displayName.trim();

  function save() {
    if (!draft || !trimmedName) return;
    const input = {
      displayName: trimmedName,
      relationship: draft.relationship.trim() || null,
      relatedPersonIds: draft.relatedPersonIds,
      notes: draft.notes.trim() || null,
      household: draft.household,
    };
    if (existing) {
      update.mutate({ id: existing.id, input });
    } else {
      create.mutate({ id: crypto.randomUUID(), ...input });
    }
    onClose();
  }

  function remove() {
    if (!existing) return;
    // Occasions that are only about this person go with them — their
    // birthday entry without them is meaningless. A shared occasion (an
    // anniversary between two people) stays for the other person.
    for (const o of linkedOccasions) {
      if (o.personIds.length === 1) delObligation.mutate({ id: o.id });
    }
    delPerson.mutate(existing.id);
    onClose();
    onDeleted?.();
  }

  const soleOccasions = linkedOccasions.filter((o) => o.personIds.length === 1).length;
  const deleteDetail = [
    soleOccasions > 0
      ? `their ${soleOccasions === 1 ? 'occasion entry' : `${soleOccasions} occasion entries`} will be deleted too`
      : null,
    linkedDocsCount > 0
      ? `${linkedDocsCount === 1 ? '1 document' : `${linkedDocsCount} documents`} will show as Unassigned`
      : null,
  ].filter(Boolean);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={isNew ? 'Add person' : 'Edit person'}>
        <div className="drawer__grip">
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">{isNew ? 'Add person' : 'Edit person'}</h2>
            <button type="button" className="btn btn--ghost btn--small" onClick={onClose}>
              Close
            </button>
          </header>
        </div>

        <div className="drawer__body">
          {existing && (
            <div className="field">
              <span className="field__label">Photo</span>
              <div className="person-photo-field">
                <PersonAvatar person={existing} className="person-photo-field__av" />
                <div className="person-photo-field__actions">
                  <label className="btn btn--secondary btn--small" htmlFor={photoInputId}>
                    {setAvatar.isPending
                      ? 'Uploading…'
                      : existing.avatarVersion
                        ? 'Change photo'
                        : 'Add photo'}
                  </label>
                  <input
                    id={photoInputId}
                    type="file"
                    accept="image/*"
                    className="file-drop__input"
                    onChange={(e) => {
                      void pickPhoto(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  {existing.avatarVersion && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => removeAvatar.mutate(existing.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {photoError && <span className="field__hint">{photoError}</span>}
            </div>
          )}

          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              type="text"
              name="person-name"
              value={draft.displayName}
              onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
              autoComplete="off"
            />
          </label>

          <label className="field">
            <span className="field__label">Relationship</span>
            <input
              className="field__input"
              type="text"
              name="person-relationship"
              value={draft.relationship}
              placeholder="son, spouse, friend…"
              onChange={(e) => setDraft({ ...draft, relationship: e.target.value })}
              autoComplete="off"
            />
            <span className="field__hint">
              How they relate to you — shows across Obligations and Documents.
            </span>
          </label>

          <label className="field person-household">
            <input
              type="checkbox"
              className="person-household__box"
              checked={draft.household}
              onChange={(e) => setDraft({ ...draft, household: e.target.checked })}
            />
            <span className="person-household__mid">
              <span className="person-household__label">Household member</span>
              <span className="field__hint person-household__hint">
                Groups first in People and leads every person picker — most documents are
                about the household.
              </span>
            </span>
          </label>

          <div className="field">
            <span className="field__label">Of (optional)</span>
            <PersonPicker
              multi
              values={draft.relatedPersonIds}
              onValuesChange={(ids) => setDraft({ ...draft, relatedPersonIds: ids })}
              placeholder="Linked to other people?"
              excludeIds={existing ? [existing.id] : []}
            />
          </div>

          {linkedOccasions.length > 0 && (
            <div className="person-editor__occasions">
              <span className="field__label">Known occasions</span>
              <ul className="person-editor__occasions-list">
                {linkedOccasions.map((o) => (
                  <li key={o.id}>{describeOccasion(o)}</li>
                ))}
              </ul>
              <span className="field__hint">
                Dates are edited on the occasion itself, in Obligations.
              </span>
            </div>
          )}

          <label className="field">
            <span className="field__label">About</span>
            <textarea
              className="field__input field__input--textarea"
              name="person-notes"
              rows={2}
              value={draft.notes}
              placeholder="Anything worth remembering about them."
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>

          {existing && (
            <div className="drawer__meta">
              <span>Added {formatStamp(existing.createdAt)}</span>
              <span>Last updated {formatStamp(existing.updatedAt)}</span>
            </div>
          )}
        </div>

        <footer className="drawer__footer">
          {confirmingDelete && existing ? (
            <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
              <span className="drawer__confirm-message">
                Delete {existing.displayName}?
                <span className="drawer__confirm-detail">
                  {deleteDetail.length > 0
                    ? `${deleteDetail.join('; ')}. `
                    : ''}
                  This can&rsquo;t be undone.
                </span>
              </span>
              <div className="drawer__confirm-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => setConfirmingDelete(false)}
                  autoFocus
                >
                  Keep
                </button>
                <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              {existing ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete
                </button>
              ) : (
                <span />
              )}
              <div className="drawer__footer-actions">
                <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={save}
                  disabled={!trimmedName}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </footer>
      </aside>
    </>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
